<template>
  <div class="px-3">
    <NuxtLink
      v-if="
        !isInIframe &&
        selectedBusiness &&
        organization?.isBusinessesEnabled &&
        organization?.organizationSettings?.businessSelectorEnabled &&
        businessesCount > 1
      "
      to="/business-selection"
      class="block rounded-md w-full p-2 mb-5 text-on-surface leading-none font-semibold cursor-pointer"
    >
      {{ selectedBusiness.name }}
    </NuxtLink>

    <Menu v-if="authUserStore.isLoggedIn && !isInIframe" :items="dropdownItems">
      <template #trigger>
        <button
          class="flex items-center h-8 rounded-md px-2 mb-6 text-on-surface leading-none font-semibold"
          data-testid="profile-dropdown"
        >
          <span
            class="bg-secondary text-on-secondary w-8 h-8 rounded-full flex items-center justify-center mr-3 text-xs font-bold"
          >
            {{ authUserStore.initials }}
          </span>
          {{ authUserStore.authUser.fullName }}
        </button>
      </template>
      <template
        v-for="option in dropdownOptions"
        :key="option.key"
        #[option.key]="{ item }"
      >
        <span class="flex items-center gap-2">
          <Icon :size="IconSize.LG" :name="option.icon" />
          {{ item.label }}
        </span>
      </template>
    </Menu>

    <Button
      v-if="authUserStore.isLoggedIn && isAdmin && !isInIframe"
      :label="$t('layout.admin-panel')"
      :loading="isNavigatingToAdmin"
      class="mb-5 w-full"
      @click="handleAdminPanelClick"
    />
    <Button
      v-if="
        !isInIframe &&
        !authUserStore.isLoggedIn &&
        organization?.displaySignInButton
      "
      :label="$t('layout.sign-in')"
      to="/auth/login"
      block
    />
  </div>
</template>

<script setup>
import { Button, Menu, Icon, IconName, IconSize } from '@exfluential/ui';
import { useAuthUserStore } from '~/store/auth-user';
import { AccountType } from '~/api/accounts';
import { apiGenerateSsoCode } from '~/api/auth';
import { useRuntimeConfig, ref, computed } from '#imports';
import { useOrganizationStore } from '~/store/organization';
import { useAccountStore } from '~/store/account';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useBusinessesStore } from '~/store/businesses';
import { useIsInIframe } from '~/composables/use-is-in-iframe';

const { t } = useI18n();
const authUserStore = useAuthUserStore();
const config = useRuntimeConfig();
const organizationStore = useOrganizationStore();
const businessesStore = useBusinessesStore();
const { organization } = storeToRefs(organizationStore);
const { businessesCount, selectedBusiness } = storeToRefs(businessesStore);
const accountStore = useAccountStore();
const { currentAccount } = storeToRefs(accountStore);
const isInIframe = useIsInIframe();
const isAdmin = computed(
  () => currentAccount.value?.type === AccountType.ADMIN,
);

const isNavigatingToAdmin = ref(false);

const handleAdminPanelClick = async () => {
  if (isNavigatingToAdmin.value) return;

  isNavigatingToAdmin.value = true;
  try {
    const { code } = await apiGenerateSsoCode();

    const adminUrl = new URL(config.public.adminAppUrl);
    adminUrl.pathname = '/auth/sso';
    adminUrl.searchParams.set('code', code);

    globalThis.location.href = adminUrl.toString();
  } catch {
    globalThis.location.href = config.public.adminAppUrl;
  } finally {
    isNavigatingToAdmin.value = false;
  }
};

const dropdownOptions = computed(() => [
  {
    key: 'profile',
    icon: IconName.PROFILE_CIRCLE,
    label: t('layout.profile'),
    to: '/profile',
  },
  {
    key: 'logout',
    icon: IconName.LOGOUT,
    label: t('layout.logout'),
    to: '/auth/logout',
  },
]);

const dropdownItems = computed(() =>
  dropdownOptions.value.map(({ key, label, to }) => ({
    label,
    to,
    slot: key,
  })),
);
</script>
